import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box } from "@mui/material";
import { AuthProvider } from "./contexts/AuthContext";
import { DivisionProvider } from "./contexts/DivisionContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SeasonProvider } from "./contexts/SeasonContext";
import { VenueProvider } from "./contexts/VenueContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import Layout from "./components/Layout";
import PublicLayout from "./components/PublicLayout";
import Login from "./pages/Login";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import Dashboard from "./pages/Dashboard";
import Players from "./pages/Players";
import LeagueDays from "./pages/LeagueDays";
import Tournaments from "./pages/Tournaments";
import TournamentDisplay from "./pages/TournamentDisplay";
import AmericanaTournamentDisplay from "./pages/AmericanaTournamentDisplay";
import TournamentInfo from "./pages/TournamentInfo";
import Matches from "./pages/Matches";
import Rankings from "./pages/Rankings";
import PublicHome from "./pages/PublicHome";
import PublicTournaments from "./pages/PublicTournaments";
import ScorekeeperRequests from "./pages/ScorekeeperRequests";
import ManageScorekeepers from "./pages/ManageScorekeepers";
import AdminManagement from "./pages/AdminManagement";
import BlockedIPs from "./pages/BlockedIPs";
import Finances from "./pages/Finances";
import PenaltiesAndBonuses from "./pages/PenaltiesAndBonuses";
import NationsLeague from "./pages/NationsLeague";
import NationsLeagueList from "./pages/NationsLeagueList";
import NationsLeaguePublic from "./pages/NationsLeaguePublic";
import NationsLeaguePublicList from "./pages/NationsLeaguePublicList";
import NationsLeagueFaq from "./pages/NationsLeagueFaq";
import Announcements from "./pages/Announcements";
import DiscountCodes from "./pages/DiscountCodes";
import Dangerous from "./pages/Dangerous";
import Transfers from "./pages/Transfers";
import CourtManagement from "./pages/CourtManagement";
import SeasonManagement from "./pages/SeasonManagement";
import MigrationPage from "./pages/MigrationPage";
import SplLanding from "./pages/SplLanding";
import LandingTwo from "./pages/LandingTwo";
import PlayerRegister from "./pages/PlayerRegister";
import PlayerRegisterWrapper from "./pages/PlayerRegisterWrapper";
import TournamentPlayerRegister from "./pages/TournamentPlayerRegister";
import TournamentShareLanding from "./pages/TournamentShareLanding";
import ResetPassword from "./pages/ResetPassword";
import SeasonRegistrationForm from "./pages/SeasonRegistrationForm";
import SeasonRegistrations from "./pages/SeasonRegistrations";
import SeasonConfirmationPage from "./pages/SeasonConfirmationPage";
import GearOrderLoginPage from "./pages/GearOrderLoginPage";
import Impressum from "./pages/Impressum";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PublicFaq from "./pages/PublicFaq";
import FaqSettings from "./pages/FaqSettings";
import VenueSettings from "./pages/VenueSettings";
import PlayerDashboard from "./pages/PlayerDashboard";
import PlayerProfile from "./pages/PlayerProfile";
import PlayerLeagueDays from "./pages/PlayerLeagueDays";
import PlayerAccountRequests from "./pages/PlayerAccountRequests";
import AccountDeletionRequests from "./pages/AccountDeletionRequests";
import TournamentRegistrationRequests from "./pages/TournamentRegistrationRequests";
import EmailSettings from "./pages/EmailSettings";
import NavigationSettings from "./pages/NavigationSettings";
import PrivacyPolicySettings from "./pages/PrivacyPolicySettings";
import PartnershipSwapSettings from "./pages/PartnershipSwapSettings";
import BadgeSettings from "./pages/BadgeSettings";
import PlayerBadges from "./pages/PlayerBadges";
import FinancialCalculator from "./pages/FinancialCalculator";
import TournamentAdmin from "./pages/TournamentAdmin";
import AmericanaTournamentAdmin from "./pages/AmericanaTournamentAdmin";
import TournamentScoreRedirect from "./pages/TournamentScoreRedirect";
import AdminGiftScan from "./pages/AdminGiftScan";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedPlayerRoute from "./components/ProtectedPlayerRoute";
import NotFound from "./pages/NotFound";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import UpdateNotification from "./components/UpdateNotification";
import PullToRefresh from "./components/PullToRefresh";
import {
  OrganizationLoadingScreen,
  OrganizationErrorScreen,
} from "./components/OrganizationLoadingScreen";
import { useOrganization } from "./contexts/OrganizationContext";
import { DevOrgSwitcher } from "./components/DevOrgSwitcher";

function AppContent() {
  const { loading, error } = useOrganization();

  if (loading) {
    return <OrganizationLoadingScreen />;
  }

  if (error) {
    return <OrganizationErrorScreen error={error} />;
  }

  return (
    <>
      <Router>
        <Routes>
          {/* Super Admin Routes */}
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route
            path="/super-admin/dashboard"
            element={<SuperAdminDashboard />}
          />

          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicLayout>
                <PublicHome />
              </PublicLayout>
            }
          />
          <Route
            path="/view-tournaments"
            element={
              <PublicLayout>
                <PublicTournaments />
              </PublicLayout>
            }
          />
          <Route
            path="/nationsLeague"
            element={<Navigate to="/cupOfNations" replace />}
          />
          <Route
            path="/cupOfNations"
            element={
              <PublicLayout>
                <NationsLeaguePublicList />
              </PublicLayout>
            }
          />
          <Route
            path="/cupOfNations/:nationsLeagueId/faq"
            element={
              <PublicLayout>
                <NationsLeagueFaq />
              </PublicLayout>
            }
          />
          <Route
            path="/cupOfNations/:nationsLeagueId"
            element={
              <PublicLayout>
                <NationsLeaguePublic />
              </PublicLayout>
            }
          />
          <Route
            path="/register"
            element={
              <PublicLayout>
                <SeasonRegistrationForm />
              </PublicLayout>
            }
          />
          <Route
            path="/landing"
            element={
              <ProtectedRoute>
                <SplLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/landingTwo"
            element={
              <ProtectedRoute>
                <LandingTwo />
              </ProtectedRoute>
            }
          />
          <Route path="/faq" element={<PublicFaq />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/season-confirm/:token"
            element={<SeasonConfirmationPage />}
          />
          <Route
            path="/season-registration/edit-gear"
            element={<GearOrderLoginPage />}
          />

          {/* Public Tournament Display */}
          <Route path="/display/:id" element={<TournamentDisplay />} />
          <Route path="/americana-display/:id" element={<AmericanaTournamentDisplay />} />

          {/* Public Tournament Info (player-facing standings/matches/bracket/my team) */}
          <Route path="/tournament/:id" element={<TournamentInfo />} />

          {/* Tournament Share / Join Link */}
          <Route path="/join/:tournamentId" element={<TournamentShareLanding />} />

          {/* Admin Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/players" element={<Players />} />
                    <Route path="/league-days" element={<LeagueDays />} />
                    <Route path="/tournaments" element={<Tournaments />} />
                    <Route
                      path="/tournaments/:tournamentId"
                      element={<TournamentAdmin />}
                    />
                    <Route
                      path="/tournaments/:tournamentId/americana"
                      element={<AmericanaTournamentAdmin />}
                    />
                    <Route path="/gift-scan" element={<AdminGiftScan />} />
                    <Route
                      path="/tournaments/:tournamentId/score"
                      element={<TournamentScoreRedirect />}
                    />
                    <Route path="/matches/:leagueDayId" element={<Matches />} />
                    <Route path="/rankings" element={<Rankings />} />
                    <Route path="/finances" element={<Finances />} />
                    <Route
                      path="/financial-calculator"
                      element={<FinancialCalculator />}
                    />
                    <Route
                      path="/penalties"
                      element={<PenaltiesAndBonuses />}
                    />
                    <Route path="/nations-league" element={<NationsLeagueList />} />
                    <Route path="/nations-league/:nationsLeagueId" element={<NationsLeague />} />
                    <Route path="/announcements" element={<Announcements />} />
                    <Route
                      path="/scorekeeper-requests"
                      element={<ScorekeeperRequests />}
                    />
                    <Route
                      path="/scorekeepers"
                      element={<ManageScorekeepers />}
                    />
                    <Route path="/admins" element={<AdminManagement />} />
                    <Route path="/blocked-ips" element={<BlockedIPs />} />
                    <Route
                      path="/player-accounts"
                      element={<PlayerAccountRequests />}
                    />
                    <Route
                      path="/deletion-requests"
                      element={<AccountDeletionRequests />}
                    />
                    <Route
                      path="/tournament-registration-requests"
                      element={<TournamentRegistrationRequests />}
                    />
                    <Route path="/transfers" element={<Transfers />} />
                    <Route path="/seasons" element={<SeasonManagement />} />
                    <Route path="/migration" element={<MigrationPage />} />
                    <Route
                      path="/season-registrations"
                      element={<SeasonRegistrations />}
                    />
                    <Route
                      path="/discount-codes"
                      element={<DiscountCodes />}
                    />
                    <Route
                      path="/court-management"
                      element={<CourtManagement />}
                    />
                    <Route path="/email-settings" element={<EmailSettings />} />
                    <Route
                      path="/navigation-settings"
                      element={<NavigationSettings />}
                    />
                    <Route
                      path="/privacy-policy-settings"
                      element={<PrivacyPolicySettings />}
                    />
                    <Route
                      path="/partnership-swap-settings"
                      element={<PartnershipSwapSettings />}
                    />
                    <Route
                      path="/badge-settings"
                      element={<BadgeSettings />}
                    />
                    <Route
                      path="/player-badges"
                      element={<PlayerBadges />}
                    />
                    <Route path="/faq-settings" element={<FaqSettings />} />
                    <Route path="/venue-settings" element={<VenueSettings />} />
                    <Route path="/dangerous" element={<Dangerous />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Player Routes */}
          <Route path="/player/register" element={<PlayerRegisterWrapper />} />
          <Route
            path="/player-registration"
            element={<TournamentPlayerRegister />}
          />
          <Route path="/player-login" element={<Navigate to="/player/login" replace />} />
          <Route path="/player/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/player/dashboard"
            element={
              <ProtectedPlayerRoute>
                <PlayerDashboard />
              </ProtectedPlayerRoute>
            }
          />
          <Route
            path="/player/league-days"
            element={
              <ProtectedPlayerRoute>
                <PlayerLeagueDays />
              </ProtectedPlayerRoute>
            }
          />
          <Route
            path="/player/profile"
            element={
              <ProtectedPlayerRoute>
                <PlayerProfile />
              </ProtectedPlayerRoute>
            }
          />

          {/* Redirect old paths to admin */}
          <Route
            path="/dashboard"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route
            path="/players"
            element={<Navigate to="/admin/players" replace />}
          />
          <Route
            path="/league-days"
            element={<Navigate to="/admin/league-days" replace />}
          />
          <Route
            path="/rankings"
            element={<Navigate to="/admin/rankings" replace />}
          />

          {/* Unknown path: show a message with the path instead of a blank page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
      <PWAInstallPrompt />
      <UpdateNotification />
      <PullToRefresh />
      <DevOrgSwitcher />
    </>
  );
}

function App() {
  return (
    <OrganizationProvider>
      <ThemeProvider>
        <Box
          sx={{
            width: "100vw",
            minHeight: "100vh",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <SeasonProvider>
            <VenueProvider>
              <DivisionProvider>
                <AuthProvider>
                  <AppContent />
                </AuthProvider>
              </DivisionProvider>
            </VenueProvider>
          </SeasonProvider>
        </Box>
      </ThemeProvider>
    </OrganizationProvider>
  );
}

export default App;
